import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Filter, Trash2 } from "lucide-react";
import {
  DestructiveConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Input,
  Label,
} from "@tellescope/ui";
import { Select, Button } from "@tellescope/design-system";
import { useInboxTableViews } from "../../../../hooks";
import { countActiveInboxFilters } from "../../utils";

const ALL_VIEW_SENTINEL = "__all__";

export interface InboxSavedViewControlsProps {
  onOpenFilterDialog: () => void;
}

/** The saved-view selector, filter trigger, delete-view and save-as-view affordances. */
export const InboxSavedViewControls = ({ onOpenFilterDialog }: InboxSavedViewControlsProps) => {
  const { t } = useTranslation("inbox");
  const {
    views,
    viewId,
    setViewId,
    isAllView,
    effectiveFilters,
    hasUnsavedChanges,
    createView,
    deleteSelectedView,
    canCreate,
    canDelete,
  } = useInboxTableViews();

  const [isSaveAsViewOpen, setIsSaveAsViewOpen] = useState(false);
  const [isDeleteViewOpen, setIsDeleteViewOpen] = useState(false);
  const [newViewTitle, setNewViewTitle] = useState("");

  const filterCount = countActiveInboxFilters(effectiveFilters);

  const handleCreateView = async () => {
    if (!newViewTitle.trim()) return;
    const newId = await createView(newViewTitle.trim(), effectiveFilters);
    setViewId(newId);
    setNewViewTitle("");
    setIsSaveAsViewOpen(false);
  };

  return (
    <>
      <Button.Group>
        <Select
          value={isAllView ? ALL_VIEW_SENTINEL : viewId}
          onValueChange={(value) => setViewId(value === ALL_VIEW_SENTINEL ? "all" : value)}
        >
          <Select.Trigger data-testid="inbox-saved-views-trigger" className="w-auto min-w-0 max-w-[140px] px-2">
            <Select.Value placeholder={t("All")} />
          </Select.Trigger>
          <Select.Content>
            <Select.Item data-testid="inbox-saved-view-item-all" value={ALL_VIEW_SENTINEL}>
              {t("All")}
            </Select.Item>
            {views.map((view) => (
              <Select.Item data-testid={`inbox-saved-view-item-${view.id}`} key={view.id} value={view.id}>
                {view.title}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
        <Button
          variant="outline"
          className="relative size-8 p-0 border-l-0"
          onClick={onOpenFilterDialog}
          hoverLabel={t("Configure filters")}
          data-trackingid="messaging-filter-dialog-open"
          data-testid="inbox-open-filter-dialog"
        >
          <Filter strokeWidth={1.8} />
          {!!filterCount && (
            <span
              className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground"
              data-testid="messaging-filter-badge"
            >
              {filterCount}
            </span>
          )}
          {hasUnsavedChanges && !filterCount && (
            <span
              data-testid="inbox-filters-unsaved-indicator"
              className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary"
            />
          )}
        </Button>
        {!isAllView && canDelete && (
          <Button
            variant="outline"
            className="size-8 p-0 border-l-0"
            onClick={() => setIsDeleteViewOpen(true)}
            hoverLabel={t("Delete View")}
            data-testid="inbox-delete-saved-view-button"
          >
            <Trash2 strokeWidth={1.8} className="text-destructive" />
          </Button>
        )}
      </Button.Group>
      {canCreate && hasUnsavedChanges && (
        <Button
          variant="outline"
          size="sm"
          className="font-light text-xs"
          onClick={() => setIsSaveAsViewOpen(true)}
          data-testid="inbox-save-as-view-button"
        >
          {t("Save as View")}
        </Button>
      )}
      <Dialog open={isSaveAsViewOpen} onOpenChange={setIsSaveAsViewOpen}>
        <DialogContent className="w-[400px] overflow-hidden" divided>
          <div className="space-y-4 px-6 pt-4 pb-2">
            <DialogTitle className="font-medium text-md">{t("Save Current View")}</DialogTitle>
            <DialogDescription className="text-xs">
              {t("Enter a name for your new view. The current filters will be saved with it.")}
            </DialogDescription>
          </div>
          <div className="flex flex-col gap-2 px-6 py-4">
            <Label>{t("View Name")}</Label>
            <Input
              value={newViewTitle}
              onChange={(e) => setNewViewTitle(e.target.value)}
              placeholder={t("e.g., High Priority Patients")}
              onKeyDownCapture={(e) => {
                if (e.key === "Enter" && newViewTitle.trim()) {
                  e.stopPropagation();
                  void handleCreateView();
                }
              }}
              data-testid="inbox-save-as-view-title"
              autoFocus
            />
          </div>
          <DialogFooter className="bg-muted">
            <Button variant="outline" onClick={() => setIsSaveAsViewOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button onClick={handleCreateView} disabled={!newViewTitle.trim()} data-testid="inbox-save-view-confirm">
              {t("Save View")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DestructiveConfirmDialog
        open={isDeleteViewOpen}
        onOpenChange={setIsDeleteViewOpen}
        title={t("Delete View")}
        description={t("This will permanently delete this saved view.")}
        onConfirm={() => deleteSelectedView()}
      />
    </>
  );
};
